import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { PRODUCT_IMAGE_FALLBACK } from "@/constants/campaignBuilder";
import type { Product } from "@/interfaces/product";
import { displayProductName } from "@/utils/product-helper";

export const TOP_TITLE_LINES = 2;
export const TOP_TITLE_LINE_HEIGHT = 1.3;
// Reserving the clamped title's height (not just clamping it) is what keeps every card in a
// row ending at the same height — a shorter title would otherwise collapse its own box.
export const productTitleSx = {
  fontFamily: "var(--font-lato)",
  fontSize: "15px",
  fontWeight: 700,
  lineHeight: TOP_TITLE_LINE_HEIGHT,
  color: "var(--color-rodeo-black)",
  display: "-webkit-box" as const,
  WebkitLineClamp: TOP_TITLE_LINES,
  WebkitBoxOrient: "vertical" as const,
  overflow: "hidden",
  minHeight: `${TOP_TITLE_LINES * TOP_TITLE_LINE_HEIGHT}em`,
};

type Props = {
  product: Product;
  onClick?: () => void;
  isEditMode?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
  onRemove?: () => void;
  position?: number;
  isAddMoreMode?: boolean;
  /** Recipient feed: when provided, shows a favorite heart (top-right) that toggles
   *  independently of the card click. Omitted in the builder (edit/add-more) modes. */
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  /** Static display (e.g. the recipient-details chosen-gift summary): no hover lift,
   *  no transition — the card is a passive block, not an affordance. Defaults to true. */
  interactive?: boolean;
  /** Builder Step 1 only: the gift came from a cheaper tier via Add More. Never passed on
   *  recipient surfaces. */
  isAddedFromLowerTier?: boolean;
};

export default function ProductCard({
  product,
  onClick,
  isEditMode = false,
  isSelected = false,
  onToggleSelect,
  onRemove,
  position,
  isAddMoreMode = false,
  isFavorite = false,
  onToggleFavorite,
  interactive = true,
  isAddedFromLowerTier = false,
}: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showFallback = failedSrc === product.imageUrl;
  const categoryLabel = product.impactTag;
  const isInteractiveMode = isEditMode || isAddMoreMode;

  function handleClick() {
    if (isAddMoreMode && onToggleSelect) {
      onToggleSelect();
    } else if (isEditMode && onToggleSelect) {
      onToggleSelect();
    } else {
      onClick?.();
    }
  }

  const cardContent = (
    <>
      <Box
        sx={{
          width: "100%",
          aspectRatio: "1 / 1",
          backgroundColor: "var(--color-white)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          padding: "22px",
          boxSizing: "border-box",
        }}
      >
        {product.imageUrl ? (
          <Box
            component="img"
            src={showFallback ? PRODUCT_IMAGE_FALLBACK : product.imageUrl}
            alt={product.name}
            onError={() => setFailedSrc(product.imageUrl ?? null)}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: showFallback ? "contain" : "cover",
              display: "block",
            }}
          />
        ) : (
          <Typography sx={{ fontSize: "28px" }}>🎁</Typography>
        )}
      </Box>

      <Box sx={{ padding: "18px 18px 18px", display: "flex", flexDirection: "column", gap: "4px" }}>
        {isAddedFromLowerTier && (
          <Typography
            sx={{
              fontFamily: "var(--font-lato)",
              fontWeight: 700,
              fontSize: "10px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: "var(--color-dark-dust)",
              lineHeight: 1.3,
              marginBottom: "2px",
            }}
          >
            Added from lower price tier
          </Typography>
        )}
        {categoryLabel && (
          <Typography
            sx={{
              fontFamily: "var(--font-lato)",
              fontWeight: 600,
              fontSize: "10px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              // Landing preview recolors the cause via the accent var; falls back to the
              // default token everywhere the var isn't set (recipient feed, collections…).
              color: "var(--lp-accent, var(--color-dust-ink))",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              marginBottom: "2px",
            }}
          >
            {categoryLabel}
          </Typography>
        )}
        <Typography sx={productTitleSx}>{displayProductName(product.name)}</Typography>
      </Box>
    </>
  );

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        borderRadius: "var(--radius-card, 10px)",
        boxShadow: "none",
        border: isSelected ? "2px solid var(--color-dark-dust)" : "1px solid var(--color-smoke)",
        backgroundColor: "var(--color-white)",
        overflow: "hidden",
        transition: interactive && !isInteractiveMode ? "box-shadow 0.2s, transform 0.2s" : "none",
        // Static summaries (interactive={false}) are passive blocks — no hover lift.
        ...(interactive && {
          "&:hover": {
            borderColor: "var(--color-dark-dust)",
            boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
            transform: "translateY(-2px)",
          },
        }),
        position: "relative",
        padding: 0,
        height: "100%",
      }}
    >
      {/* Recipient feed: favorite heart. Only outside the builder's edit/add-more
          modes (those own the top corners). Stops propagation so toggling a
          favorite never opens the product. */}
      {onToggleFavorite && !isInteractiveMode && (
        <IconButton
          aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          aria-pressed={isFavorite}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 2,
            width: 34,
            height: 34,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            boxShadow: "0 2px 8px rgba(51, 51, 51, 0.12)",
            color: isFavorite ? "var(--color-dark-dust)" : "var(--color-rodeo-dust)",
            transition: "color 0.15s, background 0.15s, transform 0.15s",
            "&:hover": {
              bgcolor: "var(--color-white)",
              color: "var(--color-rodeo-black)",
              transform: "scale(1.08)",
            },
            // R06: invisible 44×44 hit area on phones — visual size (34×34) is unchanged.
            "@media (max-width: 767px)": {
              "&::after": {
                content: '""',
                position: "absolute",
                inset: "-5px",
                borderRadius: "50%",
              },
            },
          }}
        >
          {isFavorite ? (
            <FavoriteIcon sx={{ fontSize: 18 }} />
          ) : (
            <FavoriteBorderIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      )}

      {/* Add-more mode: selection circle (no drag, no delete) */}
      {isAddMoreMode && (
        <Box
          sx={{ position: "absolute", top: 8, left: 8, zIndex: 2 }}
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        >
          <Box sx={{ bgcolor: "rgba(255,255,255,0.9)", borderRadius: "50%" }}>
            {/* Checkbox (circular icons), not Radio: a checked radio never fires change, so it could not deselect */}
            <Checkbox
              icon={<RadioButtonUncheckedIcon fontSize="small" />}
              checkedIcon={<RadioButtonCheckedIcon fontSize="small" />}
              checked={isSelected}
              onChange={onToggleSelect}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              size="small"
              sx={{
                p: "2px",
                color: "var(--color-smoke)",
                "&.Mui-checked": { color: "var(--color-dark-dust)" },
              }}
            />
          </Box>
        </Box>
      )}

      {/* Edit-mode left: selection circle + delete on hover */}
      {isEditMode && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        >
          <Box sx={{ bgcolor: "var(--color-white)", borderRadius: "50%" }}>
            <Checkbox
              icon={<RadioButtonUncheckedIcon fontSize="small" />}
              checkedIcon={<RadioButtonCheckedIcon fontSize="small" />}
              checked={isSelected}
              onChange={onToggleSelect}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              size="small"
              sx={{
                p: "2px",
                color: "var(--color-smoke)",
                "&.Mui-checked": { color: "var(--color-dark-dust)" },
              }}
            />
          </Box>

          {isHovered && onRemove && (
            <Box
              component="button"
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{
                width: 28,
                height: 28,
                bgcolor: "var(--color-white)",
                borderRadius: "50%",
                border: "1px solid var(--color-smoke)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16, color: "var(--color-good-wine)" }} />
            </Box>
          )}
        </Box>
      )}

      {/* Edit-mode right: drag handle + position number */}
      {isEditMode && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              bgcolor: "var(--color-dark-dust)",
              borderRadius: "var(--radius-btn, 6px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DragIndicatorIcon sx={{ fontSize: 14, color: "white" }} />
          </Box>

          {position !== undefined && (
            <Box
              sx={{
                width: 32,
                height: 32,
                bgcolor: "var(--color-dark-dust)",
                borderRadius: "var(--radius-btn, 6px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-lato)",
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "white",
                  lineHeight: 1,
                }}
              >
                {position}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {onClick || isInteractiveMode ? (
        <CardActionArea
          onClick={handleClick}
          aria-label={`${isEditMode || isAddMoreMode ? "Select" : "View"} ${product.name}`}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            height: "100%",
            "& .MuiCardActionArea-focusHighlight": { background: "transparent" },
          }}
        >
          {cardContent}
        </CardActionArea>
      ) : (
        <Box
          sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", height: "100%" }}
          aria-label={product.name}
        >
          {cardContent}
        </Box>
      )}
    </Card>
  );
}
